import * as React from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Sparkles, TrendingUp, Users, Zap } from "lucide-react";
import { Alert, AlertDescription, AlertTitle, Button, Label, Skeleton, Switch, useChat } from "randmarcomps";
import type { Reseller } from "randmar-api-client";
import { useGetV4ManufacturerByRouteManufacturerIdResellersQuery, useGetV4PartnerByApplicationIdAccountManufacturerQuery } from "randmar-api-client";
import ResellersGrid from "../components/grids/ResellersGrid";
import SalesSummary from "../componentsCore/cards/SalesSummary";
import { DataRefreshBar } from "../components/util/DataRefreshBar";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { formatRandmarDate } from "../utils/dates";
import withRoot from "../withRoot";

const formatCompactNumber = (value: number) => {
  const useCompact = Math.abs(value) >= 1000;
  return new Intl.NumberFormat(undefined, {
    notation: useCompact ? "compact" : "standard",
    maximumFractionDigits: useCompact ? 1 : 0,
  }).format(value);
};

const formatCompactCurrency = (value: number, currencyCode?: string | null) => {
  const useCompact = Math.abs(value) >= 1000;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode || "USD",
    notation: useCompact ? "compact" : "standard",
    maximumFractionDigits: useCompact ? 1 : 0,
  }).format(value);
};

const formatLocation = (city?: string | null, province?: string | null, country?: string | null) =>
  [city, province, country].filter(Boolean).join(", ");

const formatShortDate = (value?: number | null) => {
  return formatRandmarDate(value) ?? "No invoice yet";
};

const isQualifiedFromQualificationId = (qualificationId?: string | null) => {
  if (!qualificationId) return false;
  const compact = qualificationId.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  return compact !== "donotsell" && compact !== "suspended";
};

const isQualifiedReseller = (reseller: Reseller) => {
  const rawIsQualified = (reseller as unknown as { IsQualified?: boolean | null }).IsQualified;
  if (typeof rawIsQualified === "boolean") return rawIsQualified;
  return isQualifiedFromQualificationId(reseller.Qualification?.QualificationId);
};

function buildResellersAssistantPrompt(options: {
  applicationId: string;
  manufacturerName: string;
  filterLabel: string;
  qualifiedCount: number;
  stats: {
    total: number;
    active: number;
    onHold: number;
    total30DaySales: number;
    average30DaySales: number;
  };
  topResellers: Array<{
    id: string;
    name: string;
    location: string;
    onHold: boolean;
    packageLabel?: string | null;
    salesLast30Days: number;
    lastInvoiceDate?: number | null;
  }>;
}) {
  const lines: string[] = [
    "You are assisting with the Randmar Manufacturer Portal Resellers page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    `Manufacturer name: ${options.manufacturerName}`,
    `Current filter: ${options.filterLabel}`,
    "",
    "Network snapshot:",
    `- Total partners shown: ${options.stats.total}`,
    `- Qualified partners: ${options.qualifiedCount}`,
    `- Active (30 days): ${options.stats.active}`,
    `- On hold: ${options.stats.onHold}`,
    `- Total sales (30 days): ${options.stats.total30DaySales}`,
    `- Average sales (30 days): ${options.stats.average30DaySales}`,
    "",
    "Top resellers (by 30-day sales):",
    ...options.topResellers.map((row, index) => {
      const status = row.onHold ? "On hold" : "Active";
      const pkg = row.packageLabel ? ` • Package: ${row.packageLabel}` : "";
      const invoiceLabel = formatRandmarDate(row.lastInvoiceDate);
      const invoice = invoiceLabel ? ` • Last invoice: ${invoiceLabel}` : "";
      return `${index + 1}. ${row.name} (${row.id}) • ${status}${pkg} • ${row.location || "Location unknown"} • 30-day sales: ${row.salesLast30Days}${invoice}`;
    }),
    "",
    "Please answer questions and suggest next actions based only on the page context above.",
  ];

  return lines.join("\n");
}

const Resellers: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { sendMessageWithClear } = useChat();
  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);
  const [qualifiedOnly, setQualifiedOnly] = React.useState(false);
  const baseTitleRef = React.useRef<string | null>(null);
  const qualifiedOnlyId = React.useId();

  const {
    data: manufacturer,
    isFetching: isManufacturerFetching,
    isError: isManufacturerError,
    refetch: refetchManufacturer,
  } = useGetV4PartnerByApplicationIdAccountManufacturerQuery(
    { applicationId, withSpecification: false },
    { skip: !applicationId }
  );

  const {
    data: resellers,
    isLoading: isResellersLoading,
    isFetching: isResellersFetching,
    isError: isResellersError,
    refetch: refetchResellers,
  } = useGetV4ManufacturerByRouteManufacturerIdResellersQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );

  const isFetching = isManufacturerFetching || isResellersFetching;

  React.useEffect(() => {
    if ((resellers || manufacturer) && !isFetching) {
      setLastRefreshedAt(new Date());
    }
  }, [isFetching, manufacturer, resellers]);

  React.useEffect(() => {
    if (!baseTitleRef.current) {
      baseTitleRef.current = document.title;
    }

    const baseTitle = baseTitleRef.current || document.title;
    const manufacturerName = manufacturer?.PublicName || manufacturer?.Name;
    document.title = manufacturerName ? `Resellers — ${manufacturerName} | ${baseTitle}` : `Resellers | ${baseTitle}`;

    return () => {
      document.title = baseTitle;
    };
  }, [manufacturer?.Name, manufacturer?.PublicName]);

  const handleRefresh = React.useCallback(async () => {
    await Promise.all([refetchResellers(), refetchManufacturer()]);
  }, [refetchManufacturer, refetchResellers]);

  const sortedResellers = React.useMemo(() => {
    return [...(resellers ?? [])].sort(
      (b, a) => (a.SalesData?.SalesLast30Days ?? 0) - (b.SalesData?.SalesLast30Days ?? 0)
    );
  }, [resellers]);

  const qualifiedResellers = React.useMemo(
    () => sortedResellers.filter(isQualifiedReseller),
    [sortedResellers]
  );

  const resellersForView = React.useMemo(
    () => (qualifiedOnly ? qualifiedResellers : sortedResellers),
    [qualifiedOnly, qualifiedResellers, sortedResellers]
  );

  const stats = React.useMemo(() => {
    const total = resellersForView.length;
    const active = resellersForView.filter((reseller) => (reseller.SalesData?.SalesLast30Days ?? 0) > 0).length;
    const onHold = resellersForView.filter((reseller) => reseller.OnHold === 1).length;
    const total30DaySales = resellersForView.reduce((sum, reseller) => sum + (reseller.SalesData?.SalesLast30Days ?? 0), 0);
    const average30DaySales = total ? total30DaySales / total : 0;

    return {
      total,
      active,
      onHold,
      total30DaySales,
      average30DaySales,
      engagement: total ? (active / total) * 100 : 0,
    };
  }, [resellersForView]);

  const manufacturerName = manufacturer?.PublicName || manufacturer?.Name || "Manufacturer";
  const currencyCode = manufacturer?.CurrencyCode || undefined;

  const highlightStats = React.useMemo(
    () => [
      {
        label: qualifiedOnly ? "Qualified Network" : "Reseller Network",
        value: formatCompactNumber(stats.total),
        helper: qualifiedOnly ? "resellers ready to go" : `${formatCompactNumber(qualifiedResellers.length)} qualified`,
        Icon: Users,
      },
      {
        label: "Active (30 days)",
        value: formatCompactNumber(stats.active),
        helper: `${Math.round(stats.engagement || 0)}% engagement`,
        Icon: Zap,
      },
      {
        label: "Avg 30-Day Sales",
        value: formatCompactCurrency(stats.average30DaySales, currencyCode),
        helper: stats.total ? "per reseller" : "Awaiting orders",
        Icon: TrendingUp,
      },
    ],
    [currencyCode, qualifiedOnly, qualifiedResellers.length, stats.active, stats.average30DaySales, stats.engagement, stats.total]
  );

  const spotlightReseller = resellersForView[0];
  const supportingResellers = resellersForView.slice(1, 4);
  const pageContainerClassName = "mx-auto w-full max-w-[1440px] space-y-8 pb-10";
  const surfaceClassName = "rounded-2xl border border-gray-100 bg-white p-6 shadow-sm";

  const assistantPrompt = React.useMemo(() => {
    if (!applicationId) return null;

    const topResellers = resellersForView.slice(0, 5).map((row) => ({
      id: row.ResellerId || "unknown",
      name: row.PublicName || row.Name || "Reseller",
      location: formatLocation(row.City, row.Province, row.Country),
      onHold: row.OnHold === 1,
      packageLabel: row.Priority,
      salesLast30Days: row.SalesData?.SalesLast30Days ?? 0,
      lastInvoiceDate: row.LastInvoiceDate,
    }));

    return buildResellersAssistantPrompt({
      applicationId,
      manufacturerName,
      filterLabel: qualifiedOnly ? "Qualified only" : "All partners",
      qualifiedCount: qualifiedResellers.length,
      stats: {
        total: stats.total,
        active: stats.active,
        onHold: stats.onHold,
        total30DaySales: stats.total30DaySales,
        average30DaySales: stats.average30DaySales,
      },
      topResellers,
    });
  }, [
    applicationId,
    manufacturerName,
    qualifiedOnly,
    qualifiedResellers.length,
    resellersForView,
    stats.active,
    stats.average30DaySales,
    stats.onHold,
    stats.total,
    stats.total30DaySales,
  ]);

  if (isResellersLoading && !resellers) {
    return (
      <main className={pageContainerClassName} role="status" aria-live="polite">
        <div className={surfaceClassName}>
          <Skeleton className="h-7 w-40" />
          <Skeleton className="mt-3 h-10 w-80" />
          <Skeleton className="mt-2 h-5 w-2/3" />
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-28 w-full rounded-2xl" />
            ))}
          </div>
        </div>
        <Skeleton className="h-[520px] w-full rounded-2xl" />
      </main>
    );
  }

  return (
    <main className={pageContainerClassName} aria-busy={isFetching}>
      <header className={surfaceClassName} role="banner">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Resellers</p>
            <h1 className="text-[32px] font-semibold tracking-tight text-slate-900">Reseller network</h1>
            <p className="max-w-2xl text-[15px] text-slate-600">
              Track performance, prioritize outreach, and activate new opportunities with a single view of your reseller activity.
            </p>
          </div>
          <div className="flex flex-col gap-3 md:items-end">
            <DataRefreshBar
              align="right"
              lastRefreshedAt={lastRefreshedAt}
              isRefreshing={isFetching}
              onRefresh={handleRefresh}
              actions={(
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => assistantPrompt && sendMessageWithClear(assistantPrompt)}
                  className="rounded-xl"
                  aria-label="Open assistant with this page context"
                  disabled={!assistantPrompt}
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            />
            <div className="flex flex-wrap items-center justify-end gap-3">
              <Button asChild size="sm" className="rounded-xl">
                <Link to={Path.createOpportunity(applicationId)}>Create opportunity</Link>
              </Button>
              <Button asChild size="sm" variant="outline" className="rounded-xl">
                <Link to={Path.opportunities(applicationId)}>View pipeline</Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {(isResellersError || isManufacturerError) && (
        <Alert className="rounded-2xl border border-rose-100 bg-rose-50 shadow-sm">
          <AlertCircle className="h-4 w-4 text-rose-600" aria-hidden="true" />
          <AlertTitle className="text-slate-900">Couldn’t refresh all data</AlertTitle>
          <AlertDescription className="text-slate-700">
            Parts of this page may be out of date. Try refreshing again.
          </AlertDescription>
        </Alert>
      )}

      <section className="grid gap-6 lg:grid-cols-[2fr,1fr]" aria-label="Reseller highlights">
        <div className={[surfaceClassName, "space-y-6"].join(" ")}>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Highlights</p>
              <p className="text-[15px] text-slate-600">Quick signals from your reseller activity.</p>
            </div>
            <p className="text-[13px] text-slate-500">
              {stats.total ? `Sorted by trailing 30-day sales.` : qualifiedOnly ? "No qualified resellers yet." : "No resellers yet."}
            </p>
          </div>

          <dl className="grid gap-4 md:grid-cols-3">
            {highlightStats.map(({ label, value, helper, Icon }) => (
              <div key={label} className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--ops-accent-muted)] text-[var(--ops-accent)]">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <div className="space-y-1">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
                    <dd className="text-[24px] font-semibold text-slate-900">{value}</dd>
                    <p className="text-[13px] text-slate-500">{helper}</p>
                  </div>
                </div>
              </div>
            ))}
          </dl>
        </div>

        <aside className={surfaceClassName} aria-label="Sales pulse">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sales pulse</p>
          <p className="text-[15px] text-slate-600">Manufacturer performance snapshot</p>
          <div className="mt-4">
            <SalesSummary salesData={manufacturer?.SalesData} fiscalYearMonthStart={manufacturer?.FiscalYearMonthStart} />
          </div>
        </aside>
      </section>

      {spotlightReseller && (
        <section className="flex flex-col gap-6 lg:flex-row" aria-label="Reseller spotlight">
          <div className={[surfaceClassName, "flex-1"].join(" ")}>
            <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ops-accent)]">Spotlight reseller</p>
            <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-[24px] font-semibold text-slate-900">{spotlightReseller.PublicName || spotlightReseller.Name}</h2>
                <p className="text-[15px] text-slate-600">
                  {formatLocation(spotlightReseller.City, spotlightReseller.Province, spotlightReseller.Country) || "Location unknown"}
                </p>
                <p className="mt-2 text-[13px] text-slate-500">
                  Last invoice: <span className="font-semibold text-slate-700">{formatShortDate(spotlightReseller.LastInvoiceDate)}</span>
                </p>
              </div>
              {spotlightReseller.ResellerId && (
                <Link
                  to={Path.reseller(applicationId, spotlightReseller.ResellerId)}
                  className="text-sm font-semibold text-[color:var(--ops-accent)] hover:underline"
                >
                  View profile →
                </Link>
              )}
            </div>

            <dl className="mt-6 grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl bg-slate-50 p-4">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">30-day sales</dt>
                <dd className="mt-2 text-[20px] font-semibold text-slate-900">
                  {formatCompactCurrency(spotlightReseller.SalesData?.SalesLast30Days ?? 0, currencyCode)}
                </dd>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</dt>
                <dd className="mt-2 text-[15px] font-semibold text-slate-900">
                  {spotlightReseller.OnHold === 1 ? "On hold" : "Active"}
                </dd>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Package</dt>
                <dd className="mt-2 text-[15px] font-semibold text-slate-900">{spotlightReseller.Priority || "—"}</dd>
              </div>
            </dl>
          </div>

          <div className={[surfaceClassName, "w-full lg:w-[360px]"].join(" ")}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rising performers</p>
            <ul className="mt-4 space-y-4">
              {supportingResellers.length === 0 && (
                <li className="text-[15px] text-slate-600">We will highlight more resellers as new activity comes in.</li>
              )}
              {supportingResellers.map((reseller) => {
                const resellerName = reseller.PublicName || reseller.Name || "Reseller";
                const resellerId = reseller.ResellerId ?? "";
                const onHold = reseller.OnHold === 1;
                return (
                  <li key={reseller.ResellerId || resellerName} className="flex items-center justify-between gap-4">
                    <div>
                      {resellerId ? (
                        <Link
                          to={Path.reseller(applicationId, resellerId)}
                          className="font-medium text-slate-900 hover:text-[color:var(--ops-accent)]"
                        >
                          {resellerName}
                        </Link>
                      ) : (
                        <p className="font-medium text-slate-900">{resellerName}</p>
                      )}
                      <p className="text-[13px] text-slate-500">
                        {formatLocation(reseller.City, reseller.Province, reseller.Country) || "Location unknown"}
                      </p>
                      {onHold ? (
                        <span className="mt-2 inline-flex w-fit items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-rose-700">
                          On hold
                        </span>
                      ) : null}
                    </div>
                    <div className="text-right">
                      <p className="text-[15px] font-semibold text-slate-900">
                        {formatCompactCurrency(reseller.SalesData?.SalesLast30Days ?? 0, currencyCode)}
                      </p>
                      <p className="text-[13px] text-slate-500">30-day sales</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      <section className="space-y-4" aria-label="Resellers">
        <div className={surfaceClassName}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-lg font-semibold text-slate-900">Resellers</p>
              <p className="text-[15px] text-slate-600">
                Sort, search, and take action on the partners that match your next opportunity.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:items-end">
              <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-2">
                <Label htmlFor={qualifiedOnlyId} className="text-[13px] font-medium text-slate-700">
                  Qualified only
                </Label>
                <Switch id={qualifiedOnlyId} checked={qualifiedOnly} onCheckedChange={setQualifiedOnly} />
              </div>
              <div className="text-[13px] text-slate-500">
                {qualifiedResellers.length} qualified of {sortedResellers.length}
                {stats.onHold > 0
                  ? ` • ${stats.onHold} on hold`
                  : qualifiedOnly
                    ? " • All qualified partners active"
                    : " • All partners active"}
              </div>
            </div>
          </div>
        </div>

        <ResellersGrid
          resellers={resellersForView}
          applicationId={applicationId}
          fiscalYearMonthStart={manufacturer?.FiscalYearMonthStart}
          defaultRowsPerPage={25}
        />
      </section>
    </main>
  );
};

export default withRoot(Resellers);
